/**
 * Video capture — a ten-second narrated pan.
 *
 * Two things make this screen different from the voice one it is modelled on.
 *
 * The recorder can stop **without being asked**: ten seconds elapse, the byte
 * watchdog trips, or the tab is backgrounded. So this screen watches
 * `recorder.ended` rather than running its own countdown — a screen timing
 * itself would sit waiting on a recorder that stopped two seconds ago and
 * hand the inspector a clip they believed was still running.
 *
 * And the preview is live before recording starts, because the poster frame is
 * grabbed off it at t=0. That means two steps rather than one: `open()` on
 * mount to light the camera, `start()` on the shutter.
 */
import { useEffect, useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Btn, Chip, GestureBar, I, Ico, StatusBar, T } from '@/components/primitives';
import { useVideoRecorder } from '@/lib/device/useVideoRecorder';
import { useGeolocation } from '@/lib/device/useGeolocation';
import { useCreateVideoMutation } from '@kapture/client-core';
import { MAX_VIDEO_DURATION_MS, MAX_VIDEO_UPLOAD_BYTES } from '@kapture/domain';
import { LayoutOrLeaf } from '@/components/LayoutOrLeaf';
import { SubjectChip } from '@/components/SubjectChip';

export const Route = createFileRoute('/sessions/$sessionId/capture/video')({
  component: VideoCapturePage,
  staticData: { mobile: true, fullscreen: true },
});

function VideoCapturePage() {
  return (
    <LayoutOrLeaf routeId="/sessions/$sessionId/capture/video">
      <VideoCaptureContent />
    </LayoutOrLeaf>
  );
}

/** `mm:ss` against the ten-second ceiling — a countdown, not a stopwatch. */
function remaining(elapsedMs: number): string {
  const left = Math.max(0, MAX_VIDEO_DURATION_MS - elapsedMs);
  return `${Math.ceil(left / 1000)}s`;
}

function VideoCaptureContent() {
  const { sessionId } = Route.useParams();
  const navigate = useNavigate();
  const recorder = useVideoRecorder();
  const geo = useGeolocation();
  const createVideo = useCreateVideoMutation();

  // One save, ever. The auto-stop effect and the shutter can both reach
  // `finish()` in the same tick — the watchdog fires while the finger is
  // coming down — and two `createVideo` calls would file the same ten seconds
  // as two captures, two uploads and two AI credits.
  const savingRef = useRef(false);

  // An oversized clip is refused here rather than in the queue. See
  // `MAX_VIDEO_UPLOAD_BYTES`: the server's answer is a 413, which is terminal,
  // so the failure would land on the /sync screen hours later instead of in
  // front of the person who can just record it again.
  const [tooBig, setTooBig] = useState<number | null>(null);

  useEffect(() => {
    void recorder.open();
    void geo.request({ timeout: 4000 }).catch(() => null);
    // The hook tears its own stream down on unmount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function finish() {
    if (savingRef.current) return;
    savingRef.current = true;

    const result = await recorder.stop();
    if (!result) {
      navigate({ to: '/sessions/$sessionId', params: { sessionId } });
      return;
    }

    if (result.blob.size > MAX_VIDEO_UPLOAD_BYTES) {
      // Nothing is created: no capture row, no queued upload, no orphaned
      // blob in OPFS. The inspector re-records or walks away.
      setTooBig(result.blob.size);
      savingRef.current = false;
      return;
    }

    // `video/mp4;codecs=…` is what MediaRecorder reports; the server's
    // allowlist matches the bare type, and a parameterised one is a 415 —
    // terminal in the sync queue.
    const mime = result.mimeType.split(';')[0] || 'video/mp4';
    const capture = await createVideo.mutateAsync({
      sessionId,
      media: result.blob,
      byteSize: result.blob.size,
      extension: mime.includes('webm') ? 'webm' : 'mp4',
      mime,
      durationMs: result.durationMs,
      poster: result.poster,
      gps: geo.point ?? null,
    });
    navigate({
      to: '/sessions/$sessionId/captures/$captureId',
      params: { sessionId, captureId: capture.id },
    });
  }

  // The recorder stopped itself. Save what it captured — the inspector has
  // ten seconds of work whether or not the clock was what ended it.
  useEffect(() => {
    if (recorder.ended) void finish();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recorder.ended]);

  function close() {
    recorder.reset();
    navigate({ to: '/sessions/$sessionId', params: { sessionId } });
  }

  function reRecord() {
    setTooBig(null);
    recorder.reset();
    void recorder.open();
  }

  const isRecording = recorder.status === 'recording';
  const degraded =
    recorder.status === 'denied' ||
    recorder.status === 'unsupported' ||
    recorder.status === 'error';
  const progress = Math.min(1, recorder.elapsedMs / MAX_VIDEO_DURATION_MS);

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        background: '#000',
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: T.font,
      }}
    >
      <StatusBar dark />

      <div
        style={{
          padding: '8px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 2,
        }}
      >
        <button type="button" onClick={close} aria-label="Close" style={hudIconBtn}>
          <Ico d={I.close} size={16} />
        </button>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 5,
            minWidth: 0,
          }}
        >
          <Chip tone="hud" size="sm">
            <span style={{ fontFamily: T.mono, letterSpacing: '0.06em' }}>
              REC ·{' '}
              {sessionId
                .replace(/[^a-z0-9]/gi, '')
                .slice(0, 8)
                .toUpperCase()}{' '}
              · video
            </span>
          </Chip>
          <SubjectChip />
        </div>
        <div style={{ width: 36 }} aria-hidden />
      </div>

      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        <video
          ref={recorder.videoRef}
          playsInline
          // The preview is muted, always: the microphone is feeding the
          // recording, and an unmuted preview loops it straight back into the
          // room as feedback while the inspector is trying to narrate.
          muted
          autoPlay
          style={{ width: '100%', height: '100%', objectFit: 'cover', background: '#000' }}
        />

        {tooBig !== null && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
              padding: 24,
              textAlign: 'center',
              background: 'rgba(0,0,0,0.82)',
            }}
          >
            <Ico d={I.warn} size={28} />
            <div style={{ fontSize: 14, fontWeight: 600 }}>Clip is too large to send</div>
            <div
              style={{
                fontSize: 12,
                color: 'rgba(255,255,255,0.7)',
                lineHeight: 1.5,
                maxWidth: 300,
              }}
            >
              {(tooBig / (1024 * 1024)).toFixed(1)} MB, and the limit is{' '}
              {Math.round(MAX_VIDEO_UPLOAD_BYTES / (1024 * 1024))} MB. Nothing was saved. A shorter
              pan, or one with less movement in frame, will fit.
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <Btn tone="light" size="md" onClick={reRecord}>
                Record again
              </Btn>
              <Btn tone="hud" size="md" onClick={close}>
                Back to session
              </Btn>
            </div>
          </div>
        )}

        {degraded && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
              padding: 24,
              textAlign: 'center',
              background: 'rgba(0,0,0,0.72)',
            }}
          >
            <Ico d={I.warn} size={28} />
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', lineHeight: 1.5 }}>
              {recorder.error ?? 'Camera unavailable in this browser.'}
            </div>
            <Btn tone="light" size="md" onClick={close}>
              Back to session
            </Btn>
          </div>
        )}

        {/* A silent clip is a fact the inspector has to know *before* they
            narrate ten seconds of nothing, not after. */}
        {recorder.muted && !degraded && (
          <div style={{ position: 'absolute', top: 12, left: 0, right: 0, textAlign: 'center' }}>
            <Chip tone="hud" size="sm" icon={<Ico d={I.warn} size={10} />}>
              No microphone — this clip will be silent
            </Chip>
          </div>
        )}

        {isRecording && (
          <div
            style={{
              position: 'absolute',
              bottom: 16,
              left: 0,
              right: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
              padding: '0 32px',
            }}
          >
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 10px',
                borderRadius: 100,
                background: 'rgba(220,38,38,0.24)',
                border: `1px solid ${T.danger}`,
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: 4,
                  background: T.danger,
                  animation: 'kap-pulse 1.4s ease-in-out infinite',
                }}
              />
              REC · {remaining(recorder.elapsedMs)} left
            </div>
            {/* The bar is the honest one: it tracks elapsed time, and the
                recording can still end early if the byte watchdog trips. */}
            <div
              style={{
                width: '100%',
                height: 3,
                borderRadius: 2,
                background: 'rgba(255,255,255,0.2)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${progress * 100}%`,
                  height: '100%',
                  background: T.danger,
                  transition: 'width 100ms linear',
                }}
              />
            </div>
          </div>
        )}
      </div>

      <div
        style={{
          padding: '14px 20px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <button
          type="button"
          onClick={() => (isRecording ? void finish() : void recorder.start())}
          disabled={recorder.status !== 'ready' && !isRecording}
          aria-label={isRecording ? 'Stop recording' : 'Start recording'}
          style={{
            width: 74,
            height: 74,
            borderRadius: 37,
            background: isRecording ? T.danger : 'rgba(255,255,255,0.12)',
            border: `3px solid ${isRecording ? T.danger : 'rgba(255,255,255,0.85)'}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            cursor: recorder.status === 'ready' || isRecording ? 'pointer' : 'not-allowed',
            opacity: recorder.status === 'ready' || isRecording ? 1 : 0.5,
          }}
        >
          <Ico d={isRecording ? I.stop : I.video} size={26} />
        </button>
      </div>
      <GestureBar dark />
    </div>
  );
}

const hudIconBtn = {
  width: 36,
  height: 36,
  borderRadius: 10,
  background: 'rgba(255,255,255,0.08)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  border: 'none',
  cursor: 'pointer',
} as const;
